/**
 * Plugin manifest schema + compatibility check.
 *
 * A plugin's manifest.json declares its identity, the bundle URL, and the
 * engine versions it expects. The host enforces compatibility before calling
 * viewer.use() so a v0.2 plugin can't accidentally run against v1.0 engine
 * code that may have removed the API surface it depends on.
 *
 * Semver matching is intentionally minimal — we only support the two ranges
 * that actually appear in this codebase ("^MAJOR.MINOR.PATCH" and
 * ">=A.B.C <X.Y.Z"). Anything else falls through with a warning. If we ever
 * need full npm semver, drop in `semver` as a dep — but that's 50KB minified
 * for one method, and the marketplace v1 only needs caret ranges.
 */
/**
 * Discriminator for what kind of host the manifest is targeting.
 * - `viewer-plugin` — installs into a `Viewer` via `viewer.use()` (admin / dashboard cell).
 * - `dashboard-widget` — registers as a cell type in the supervision dashboard.
 *
 * Missing `kind` is treated as `viewer-plugin` for backward compat with older
 * marketplace entries.
 */
export type PluginKind = 'viewer-plugin' | 'dashboard-widget';

/**
 * What the thing IS to a user — where they meet it. `app` is opened as a
 * destination and owns the screen; `tool` augments whatever scene is already
 * open; `dashboard` is a cell on the supervision dashboard's board.
 *
 * `dashboard` is the one type a `dashboard-widget` can carry, and the store
 * stamps it on every widget it publishes, so the two fields cannot disagree:
 * `kind` names the host contract (which loader runs it), `type` names the
 * shelf a user finds it on. See PluginManifest.type.
 */
export type PluginType = 'app' | 'tool' | 'dashboard';

/** Every legal PluginType, for validators — a closed set, not a free string. */
export const PLUGIN_TYPES: readonly PluginType[] = ['app', 'tool', 'dashboard'];

export interface PluginManifest {
  /** Stable id used as the snapshot.extensions key and the viewer.plugins map key. */
  id: string;
  /**
   * Host kind. Default `'viewer-plugin'`. Marketplace v2 added `'dashboard-widget'`
   * to host cell widgets in the construction-supervision dashboard, gated by the
   * same `/plugins/registry.json` + WebDAV storage.
   */
  kind?: PluginKind;
  /** Display label. */
  label: string;
  /** Plugin package version (semver). The id is stable across versions. */
  version: string;
  /** One-line description shown in the plugin browser. */
  description?: string;
  /** Author name / organisation, surfaced in the browser UI. */
  author?: string;
  /** URL to the plugin's ES module bundle, relative to the registry's host. */
  entryUrl: string;
  /** Optional screenshot URLs, relative to the registry's host. */
  screenshots?: string[];
  /**
   * Engine version ranges this plugin was tested against. The host refuses
   * to install if any declared engine fails the check. Keys are package names
   * the plugin expects to import; values are semver range strings.
   */
  engines: {
    '@ais/viewer-core'?: string;
    '@ais/viewer-ui'?: string;
    three?: string;
  };
  /**
   * Optional list of host adapter keys the plugin requires (e.g. ['sidebar']).
   * The host refuses to install if any required adapter isn't mounted on
   * viewer.ui. Lets dashboard cells (which mount no Sidebar) skip plugins
   * that only contribute UI — their state still applies via getState/applyState.
   */
  contributes?: Array<'sidebar' | 'iconbar' | 'overlay' | 'dropzone' | 'splitView'>;
  /**
   * What the thing IS to a user: an `app` is opened as a destination and owns
   * the screen; a `tool` augments whatever scene is already open; a
   * `dashboard` widget sits as a cell on the supervision dashboard's board.
   * That decides where a plugin appears — apps are pickable on a launcher,
   * tools are not, dashboard widgets are picked from a board's cell menu.
   *
   * Optional. PluginHost itself never gates on it, and absent means
   * unclassified, which must read as "show it". A hub's FULL editor does read
   * it: apps are left to their doors rather than piled into the editor
   * (an app that reshapes the editor on install would otherwise own every
   * plain visit) — a scene opens AS an app through `installApp`.
   *
   * `dashboard` overlaps `kind: 'dashboard-widget'` on purpose, and the store
   * keeps them in step rather than letting them disagree: a widget is stamped
   * `dashboard` when published (it is the only type a widget may carry), and a
   * viewer-plugin may not claim it. There is still NO separate
   * `category: '3d' | '2d'` field — `kind` names the host contract, `type`
   * names the shelf, and a widget's shelf is the dashboard.
   */
  type?: PluginType;
  /**
   * DECLARED UI NEEDS — the sidebar sections (and, later, other bar state) this
   * plugin needs visible to be usable. Authored UP FRONT, before the code, the
   * way an Android manifest declares permissions: the dev then develops against
   * exactly this surface, so the declaration stays minimal because anything they
   * left out is missing while they work.
   *
   * Deliberately the SAME shape as `AppConfig.ui` (viewer-core) so a host can
   * hand it to `applyUiState` unchanged — one vocabulary, two carriers.
   *
   * It is a REQUEST, never an override: a host composes it with its own policy
   * (deployment/role) and the entitlement above that, and restriction always
   * wins. Otherwise a marketplace plugin could reveal a section a deployment
   * deliberately hid.
   */
  ui?: { sidebar?: { sections?: string[] } } & Record<string, unknown>;
  /**
   * Other plugins this one needs alongside it — plugin ids, not adapter keys
   * (`contributes` covers the host surfaces). An app that composes a tool's
   * output declares it here: `consultant-101` depends on the geo-data plugin for
   * its ground model, and saying so is what lets a store offer them together and
   * a host warn instead of half-working.
   *
   * DECLARATIVE for now: the host does not auto-install these, so a plugin must
   * still degrade honestly when a dependency is absent. Resolution can come
   * later without changing this field's meaning.
   */
  dependsOn?: string[];
}

/** Top-level shape of /plugins/registry.json. */
export interface PluginRegistry {
  plugins: PluginRegistryEntry[];
}

/**
 * A registry entry is the manifest plus its absolute manifest URL. The host
 * fetches the manifest (which may be a richer object than the registry index
 * carries) before deciding whether to install.
 */
export interface PluginRegistryEntry {
  id: string;
  /** Mirrors `PluginManifest.kind`. Missing = `'viewer-plugin'`. */
  kind?: PluginKind;
  version: string;
  label: string;
  description?: string;
  author?: string;
  manifestUrl: string;
  /**
   * Mirrored from the manifest so a store or host can filter the INDEX without
   * fetching every manifest first — the point of an index. Absent means
   * unclassified, which must render as "show it" rather than "hide it": every
   * entry published before this field existed has none. (A `dashboard-widget`
   * entry carries `'dashboard'` — see PluginManifest.type.)
   */
  type?: PluginType;
}

/**
 * Parse a semver string ("0.2.0") into [major, minor, patch]. Returns null
 * for unparseable input — caller decides whether to refuse install or warn.
 */
function parseVersion(v: string): [number, number, number] | null {
  const m = v.trim().match(/^(\d+)\.(\d+)\.(\d+)/);
  if (!m) return null;
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

/**
 * Test whether `actual` satisfies `range`. Supports two range formats:
 *   - "^A.B.C"            → "≥ range minimum, < range major bound"
 *   - ">=A.B.C <X.Y.Z"    → explicit two-bound range
 * Anything else is treated as exact-match.
 *
 * The pre-1.0 caret rule is deliberately LOOSER than npm's strict caret. The
 * marketplace policy for `0.x` engines is "minor bumps are additive — new
 * fields/events, no removals" (see CLAUDE.md guideline #4 and the
 * model-hierarchy plan's 0.7.0 bump). Under strict npm caret, every minor bump
 * (0.6 → 0.7) would invalidate every published plugin declaring `^0.6.0`,
 * forcing a re-publish loop. We instead accept any `0.y.z` with `y >= range
 * minor` (and within the same major axis), so additive 0.x growth doesn't
 * break the marketplace. Authors who genuinely need a tighter range can write
 * `">=0.6.0 <0.7.0"` explicitly.
 */
export function satisfies(actual: string, range: string): boolean {
  const av = parseVersion(actual);
  if (!av) return false;
  const r = range.trim();

  // Caret range: "^A.B.C" → "≥ A.B.C, < next major axis".
  const caret = r.match(/^\^(\d+)\.(\d+)\.(\d+)/);
  if (caret) {
    const [maj, min, pat] = [Number(caret[1]), Number(caret[2]), Number(caret[3])];
    if (av[0] !== maj) return false;
    if (av[1] < min) return false;
    if (av[1] === min && av[2] < pat) return false;
    return true;
  }

  // Two-bound: ">=0.2.0 <1.0.0".
  const bounds = r.match(/^>=\s*(\d+\.\d+\.\d+)\s+<\s*(\d+\.\d+\.\d+)/);
  if (bounds) {
    const lo = parseVersion(bounds[1]);
    const hi = parseVersion(bounds[2]);
    if (!lo || !hi) return false;
    const cmp = (a: [number, number, number], b: [number, number, number]): number => {
      for (let i = 0; i < 3; i++) if (a[i] !== b[i]) return a[i] - b[i];
      return 0;
    };
    return cmp(av, lo) >= 0 && cmp(av, hi) < 0;
  }

  // Fallback: exact match.
  return r === actual;
}

/** Validate a manifest's shape. Throws with a readable message on missing fields. */
export function validateManifest(raw: unknown): PluginManifest {
  if (!raw || typeof raw !== 'object') throw new Error('manifest must be an object');
  const m = raw as Record<string, unknown>;
  for (const k of ['id', 'label', 'version', 'entryUrl'] as const) {
    if (typeof m[k] !== 'string' || !(m[k] as string)) {
      throw new Error(`manifest missing required string field '${k}'`);
    }
  }
  if (!m.engines || typeof m.engines !== 'object') {
    throw new Error("manifest missing 'engines' object");
  }
  return m as unknown as PluginManifest;
}
